<script setup lang="ts">
interface WorkOrder {
  id: number
  name?: string
}

interface Campaign {
  id: number
  name: string
  work_orders?: WorkOrder[]
}

interface IProps {
  campaigns: Campaign[]
  workOrdersIncluded: boolean
  openedNodes: string[]
  selectedCampaignIds: number[]
  selectedWorkOrdersByCampaign: Record<number, number[]>
}

const props = defineProps<IProps>()

interface CustomEvents {
  (e: 'toggle-campaign', campaign: Campaign): void
  (e: 'toggle-open', campaignId: number): void
  (e: 'toggle-work-order', payload: { campaign: Campaign, workOrderId: number }): void
}

const emit = defineEmits<CustomEvents>()

const campaignNodeValue = (campaignId: number) => `campaign:${campaignId}`

const isCampaignOpen = (campaignId: number) => {
  return props.openedNodes.includes(campaignNodeValue(campaignId))
}

const isCampaignSelected = (campaignId: number) => {
  return props.selectedCampaignIds.includes(campaignId)
}

const isCampaignChecked = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return isCampaignSelected(campaign.id)

  const selectedWorkOrders = props.selectedWorkOrdersByCampaign[campaign.id] ?? []
  const allWorkOrderIds = campaign.work_orders.map(workOrder => workOrder.id)

  if (!allWorkOrderIds.length)
    return isCampaignSelected(campaign.id)

  return allWorkOrderIds.every(workOrderId => selectedWorkOrders.includes(workOrderId))
}

const isCampaignIndeterminate = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return false

  const selectedWorkOrders = props.selectedWorkOrdersByCampaign[campaign.id] ?? []
  const allWorkOrderIds = campaign.work_orders.map(workOrder => workOrder.id)
  const selectedCount = allWorkOrderIds.filter(workOrderId => selectedWorkOrders.includes(workOrderId)).length

  return selectedCount > 0 && selectedCount < allWorkOrderIds.length
}

const isWorkOrderSelected = (campaignId: number, workOrderId: number) => {
  return (props.selectedWorkOrdersByCampaign[campaignId] ?? []).includes(workOrderId)
}

</script>

<template>
  <VList class="multiple-campaigns-select__tree">
    <template v-for="campaign in campaigns" :key="campaign.id">
      <VListItem :class="['multiple-campaigns-select__campaign', { 'selected-campaign': isCampaignChecked(campaign) }]"
        @click="emit('toggle-campaign', campaign)">
        <template #prepend>
          <VListItemAction start>
            <VCheckboxBtn :model-value="isCampaignChecked(campaign)" :indeterminate="isCampaignIndeterminate(campaign)"
              @click.stop="emit('toggle-campaign', campaign)" />
          </VListItemAction>
        </template>

        <VListItemTitle class="multiple-campaigns-select__campaign__title">
          {{ campaign.name }}
        </VListItemTitle>

        <template #append>
          <VBtn v-if="workOrdersIncluded && campaign.work_orders?.length" icon variant="text" size="x-small"
            @click.stop="emit('toggle-open', campaign.id)">
            <VIcon :icon="isCampaignOpen(campaign.id) ? 'tabler-chevron-up' : 'tabler-chevron-down'" />
          </VBtn>
        </template>
      </VListItem>

      <div v-if="workOrdersIncluded && campaign.work_orders?.length && isCampaignOpen(campaign.id)"
        class="multiple-campaigns-select__children">
        <VListItem v-for="workOrder in campaign.work_orders" :key="workOrder.id"
          class="multiple-campaigns-select__child"
          @click="emit('toggle-work-order', { campaign, workOrderId: workOrder.id })">
          <template #prepend>
            <VListItemAction start>
              <VCheckboxBtn :model-value="isWorkOrderSelected(campaign.id, workOrder.id)"
                @click.stop="emit('toggle-work-order', { campaign, workOrderId: workOrder.id })" />
            </VListItemAction>
          </template>

          <VListItemTitle>
            {{ workOrder.name }}
          </VListItemTitle>
        </VListItem>
      </div>
    </template>
  </VList>
</template>
